/**
 * LifecycleFunnel — Feb 2026 · Iter93
 *
 * Compact horizontal funnel showing archived-by-reason distribution.
 * Sits between the CRS Health banner and the project grid whenever the
 * user turns on `View archived`. Reads its data from the
 * `/api/projects/counts` endpoint (`by_reason` object), so no
 * extra network round-trip.
 */
import { useMemo } from "react";

const REASONS = [
  { key: "won",       l: "🏆 Won",     colour: "#059669" },  // emerald-600
  { key: "lost",      l: "😔 Lost",    colour: "#e11d48" },  // rose-600
  { key: "paused",    l: "⏸ Paused",  colour: "#d97706" },  // amber-600
  { key: "completed", l: "✅ Done",    colour: "#475569" },  // slate-600
  { key: "other",     l: "❓ Other",   colour: "#334155" },  // slate-700
];

export default function LifecycleFunnel({ byReason }) {
  const { rows, total, winRate } = useMemo(() => {
    const total = REASONS.reduce((s, r) => s + (byReason?.[r.key] || 0), 0);
    const rows = REASONS.map((r) => ({
      ...r, n: byReason?.[r.key] || 0,
      pct: total ? Math.round((byReason?.[r.key] || 0) / total * 1000) / 10 : 0,
    }));
    const won   = byReason?.won  || 0;
    const lost  = byReason?.lost || 0;
    const winRate = (won + lost) ? Math.round(won / (won + lost) * 100) : null;
    return { rows, total, winRate };
  }, [byReason]);

  if (!total) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white/60 px-4 py-3
                          text-[11.5px] text-slate-500 flex items-center gap-2"
              data-testid="lifecycle-funnel-empty">
        <span className="font-heading font-black tracking-widest uppercase text-slate-700">
          Lifecycle Funnel
        </span>
        <span>· No archived projects yet · archive a project to see the funnel</span>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-4 py-3"
             data-testid="lifecycle-funnel">
      <div className="flex items-center gap-3 mb-2">
        <span className="font-heading font-black tracking-widest uppercase text-[11px] text-slate-700">
          Lifecycle Funnel
        </span>
        <span className="text-[10.5px] font-mono text-slate-500">
          {total} archived project{total === 1 ? "" : "s"}
        </span>
        {winRate != null && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full
                             bg-emerald-50 text-emerald-700 border border-emerald-200
                             px-2 py-0.5 text-[10.5px] font-mono uppercase tracking-widest"
                data-testid="lifecycle-funnel-winrate">
            Win-rate {winRate}%
          </span>
        )}
      </div>
      {/* Stacked bar */}
      <div className="flex h-6 rounded overflow-hidden border border-slate-200"
                data-testid="lifecycle-funnel-bar">
        {rows.filter((r) => r.n > 0).map((r) => (
          <div
            key={r.key}
            style={{ width: `${r.pct}%`, background: r.colour }}
            className="flex items-center justify-center text-white text-[10.5px] font-bold"
            title={`${r.l} · ${r.n} · ${r.pct}%`}
            data-testid={`lifecycle-funnel-segment-${r.key}`}
          >
            {r.pct >= 8 ? `${r.pct}%` : ""}
          </div>
        ))}
      </div>
      {/* Legend */}
      <div className="mt-2 flex items-center flex-wrap gap-x-4 gap-y-1 text-[10.5px] font-mono">
        {rows.filter((r) => r.n > 0).map((r) => (
          <span key={r.key} className="inline-flex items-center gap-1.5 text-slate-600"
                data-testid={`lifecycle-funnel-legend-${r.key}`}>
            <span className="inline-block w-2 h-2 rounded-sm" style={{ background: r.colour }} />
            {r.l} · {r.n}
          </span>
        ))}
      </div>
    </div>
  );
}
